import { useEffect, useRef, useState } from 'react'
import { PopupModal, useCalendlyEventListener } from 'react-calendly'
import { useIntl } from 'react-intl'

import { CALENDLY_LINK } from '@/config/ui'
import { useNotificationContext } from '@/context/NotificationContext'
import { useWorkspaceContext } from '@/context/WorkspaceContext'
import { buildI18nFunction } from '@/utils/messages'
import { composePathAndParams } from '@/utils/urls'

type CalendlyModalProps = {
  selector?: string
  children: (data: {
    isOpen: boolean
    toggleCalendlyModal: (flag: boolean) => void
  }) => JSX.Element | React.ReactNode
}

export function CalendlyModal({
  selector = '__next',
  children,
}: CalendlyModalProps) {
  const [isOpen, setIsOpen] = useState(false)

  const t = buildI18nFunction(useIntl().formatMessage)

  const { notification } = useNotificationContext()

  const handleEventsScheduled = () => {
    setIsOpen(false)
    notification.success({
      message: t('PaywallModal.calendly.success.message').toString(),
    })
  }

  useCalendlyEventListener({
    onEventScheduled: handleEventsScheduled,
  })
  const ref = useRef()

  const toggleCalendlyModal = (flag: boolean) => setIsOpen(flag)

  useEffect(() => {
    ref.current = document.querySelector(selector)
  }, [selector])

  const date = new Date()

  const {
    user: { email, name },
  } = useWorkspaceContext()

  const calendlyLink = composePathAndParams(CALENDLY_LINK, {
    name: name,
    month: `${date.getFullYear()}-${date.getMonth() + 1}`,
    email: email,
  })

  return (
    <>
      <PopupModal
        url={calendlyLink}
        onModalClose={() => setIsOpen(false)}
        open={isOpen}
        rootElement={
          typeof window !== 'undefined'
            ? document.getElementById('__next')
            : null
        }
      />
      {children({ isOpen, toggleCalendlyModal })}
    </>
  )
}
